import { RouterModule, Routes } from '@angular/router';

import { DealerResolver } from '@app/core/resolvers/dealer.resolver';

import { AuthenticatePassThroughComponent } from '@app/core/components/auth/authenticate-pass-through/authenticate-pass-through.component';
import { ConfigurePrivacyPolicyComponent } from './components/legal-info/privacy-policy/privacy-policy.component';
import { ConfigureCookiePolicyComponent } from './components/legal-info/cookie-policy/cookie-policy.component';
import { ConfigurePurchaseVehicleComponent } from './components/purchase-vehicle/purchase-vehicle.component';
import { ConfigureCustomiseReviewComponent } from './components/customise-review/customise-review.component';
import { ConfigureDeliveryOptionsComponent } from './components/delivery-options/delivery-options.component';
import { ConfigureFullBreakdownComponent } from './components/modals/full-breakdown/full-breakdown.component';
import { ConfigureFinanceSkipModalComponent } from './components/modals/finance-skip/finance-skip.component';
import { ConfigureTermsOfUseComponent } from './components/legal-info/terms-of-use/terms-of-use.component';
import { ConfigureFinanceOptionsComponent } from './components/finance-options/finance-options.component';
import { AuthenticateComponent } from '@app/core/components/auth/authenticate/authenticate.component';
import { ConfigurePartExchangeComponent } from './components/part-exchange/part-exchange.component';
import { InvalidateComponent } from '@app/core/components/auth/invalidate/invalidate.component';
import { ConfigureContinueComponent } from './components/continue/continue.component';
import { ConfigurePaymentComponent } from './components/payment/payment.component';
import { ConfigureComponent } from './components/configure.component';

import { ConfigurePartExchangeValuationModalComponent } from './components/modals/part-exchange-valuation/part-exchange-valuation.component';
import { ConfigureCustomerDetailsModalComponent } from './components/modals/customer-details/customer-details.component';
import { DemoFinanceUpdateModalComponent } from './components/modals/demo-finance-update/demo-finance-update.component';
import { ConfigureFinanceLockedModalComponent } from './components/modals/finance-locked/finance-locked.component';
import { ConfigureFinanceStatusModalComponent } from './components/modals/finance-status/finance-status.component';
import { ConfigureFinanceApplyModalComponent } from './components/modals/finance-apply/finance-apply.component';
import { ConfigureFinanceInfoModalComponent } from './components/modals/finance-info/finance-info.component';
import { ConfigureSoftErrorModalComponent } from './components/modals/soft-error/soft-error.component';
import { ConfigureCallbackModalComponent } from './components/modals/callback/callback.component';
import { ConfigureWelcomeModalComponent } from './components/modals/welcome/welcome.component';
import { ConfigureAddonModalComponent } from './components/modals/addon/addon.component';

import { EnquiryAuthenticateResolver } from '@app/core/resolvers/enquiry-authenticate.resolver';
import { ConfigureVehicleResolver } from '@app/core/resolvers/configure-vehicle.resolver';
import { ConfigureEnquiryResolver } from '@app/core/resolvers/configure-enquiry.resolver';
import { ConfigureFinanceFormModalComponent } from './components/modals/finance-form/finance-form.component';

const routes: Routes = [
	// MODAL OUTLET

	{
		path: 'welcome',
		component: ConfigureWelcomeModalComponent,
	},
	{
		path: 'part-exchange-valuation',
		component: ConfigurePartExchangeValuationModalComponent,
	},
	{
		path: 'finance-apply',
		component: ConfigureFinanceApplyModalComponent,
	},
	{
		path: 'finance-form',
		component: ConfigureFinanceFormModalComponent,
	},
	{
		path: 'full-breakdown',
		component: ConfigureFullBreakdownComponent,
	},
	{
		path: 'finance-locked',
		component: ConfigureFinanceLockedModalComponent,
	},
	{
		path: 'finance-status',
		component: ConfigureFinanceStatusModalComponent,
	},
	{
		path: 'finance-info',
		component: ConfigureFinanceInfoModalComponent,
	},
	{
		path: 'finance-skip',
		component: ConfigureFinanceSkipModalComponent,
	},
	{
		path: 'callback',
		component: ConfigureCallbackModalComponent,
	},
	{
		path: 'addon',
		component: ConfigureAddonModalComponent,
	},
	{
		path: 'soft-error',
		component: ConfigureSoftErrorModalComponent,
	},
	{
		path: 'customer-details/vehicle/:vehicle_id',
		component: ConfigureCustomerDetailsModalComponent,
	},
	{
		path: 'customer-details/:mode',
		component: ConfigureCustomerDetailsModalComponent,
	},
	{
		path: 'demo-finance-update',
		component: DemoFinanceUpdateModalComponent,
	},

	// PRIMARY OUTLET

	// Non vehicle or enquiry routes

	{
		path: 'terms-of-use',
		component: ConfigureTermsOfUseComponent,
		resolve: [DealerResolver],
	},
	{
		path: 'privacy-policy',
		component: ConfigurePrivacyPolicyComponent,
		resolve: [DealerResolver],
	},
	{
		path: 'cookie-policy',
		component: ConfigureCookiePolicyComponent,
		resolve: [DealerResolver],
	},
	{
		path: 'session-ended',
		component: InvalidateComponent,
		resolve: [DealerResolver],
	},

	// Vehicle routes

	{
		path: 'v/:vehicle_id/p/:enquiry_hash/authenticate',
		component: AuthenticateComponent,
		resolve: [EnquiryAuthenticateResolver],
		data: { success_path: '/configure/v/:vehicle_id', forget_path: '/configure/v/:vehicle_id', trigger: 'configure', can_start_again: true },
	},
	{
		path: 'v/:vehicle_id',
		component: ConfigureComponent,
		resolve: [ConfigureVehicleResolver],
		children: [
			{
				path: '',
				redirectTo: 'your-new-vehicle',
				pathMatch: 'full',
			},
			{
				path: 'your-new-vehicle',
				component: ConfigurePurchaseVehicleComponent,
			},
		],
	},

	// Enquiry routes

	{
		path: 'e/:enquiry_hash/authenticate',
		component: AuthenticateComponent,
		resolve: [EnquiryAuthenticateResolver],
		data: { success_path: '/configure/e/:enquiry_hash', forget_path: '/configure/v/:vehicle_id', trigger: 'configure', can_start_again: true },
	},
	{
		path: 'e/:enquiry_hash/authenticate/:access_code',
		component: AuthenticatePassThroughComponent,
		data: { success_path: '/configure/e/:enquiry_hash', failed_path: '/configure/e/:enquiry_hash/authenticate' },
	},
	{
		path: 'e/:enquiry_hash',
		component: ConfigureComponent,
		resolve: [ConfigureEnquiryResolver],
		children: [
			{
				path: '',
				redirectTo: 'continue',
				pathMatch: 'full',
			},
			{
				path: 'continue',
				component: ConfigureContinueComponent,
			},
			{
				path: 'your-new-vehicle',
				component: ConfigurePurchaseVehicleComponent,
			},
			{
				path: 'part-exchange',
				component: ConfigurePartExchangeComponent,
			},
			{
				path: 'customise-review',
				component: ConfigureCustomiseReviewComponent,
			},
			{
				path: 'finance-options',
				component: ConfigureFinanceOptionsComponent,
			},
			{
				path: 'delivery-options',
				component: ConfigureDeliveryOptionsComponent,
			},
			{
				path: 'payment-options',
				component: ConfigurePaymentComponent,
			},
		],
	},

	// NOT FOUND

	{
		path: '',
		redirectTo: '/not-found',
		pathMatch: 'full',
	},
];

export const ConfigureRouterModule = RouterModule.forChild(routes);
